import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import ErrorHandler from "@components/ErrorHandler/ErrorHandler";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import type { FilterFieldDefinition } from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { CargoOperationGridRow } from "@services/drybulkCargoOperationsApi";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { DRYBULK_CARGO_OPS_LANG_BASE, OPERATION_STATUSES } from "../constants";
import {
  GRID_PAGE_SIZE_OPTIONS,
  useCargoOperationsSearch,
} from "../hooks/useCargoOperationsSearch";
import { useDeleteCargoOperation } from "../hooks/useDeleteCargoOperation";
import { useGradeOptions } from "../hooks/useGradeOptions";
import { useIsCargoOpsShipper } from "../hooks/useIsCargoOpsShipper";
import { useIsCargoOpsShippingAgent } from "../hooks/useIsCargoOpsShippingAgent";
import { useVesselCallOperationContext } from "../hooks/useVesselCallOperationContext";
import { cargoOpsOperationPath } from "../utils/cargoOpsPaths";
import { formatGridInstant } from "../utils/formatInstant";
import { operationTypeOptions } from "../utils/operationTypeOptions";
import { operationStatusBadgeType } from "../utils/statusBadgeType";
import { statusLabel } from "../utils/statusLabel";
import AddOperationAction from "./AddOperationAction";
import DeleteDraftDialog from "./DeleteDraftDialog";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.list`;

const DrybulkCargoOperationsList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { vesselCallId, vesselImo } = useVesselCallOperationContext();
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const grid = useCargoOperationsSearch({ pausePolling: pendingDelete !== null });
  const deleteOperation = useDeleteCargoOperation();
  const isShippingAgent = useIsCargoOpsShippingAgent();
  const isShipper = useIsCargoOpsShipper();
  const gradeOptions = useGradeOptions({ enabled: !isShipper });
  const isOperationsUser = useAuthStore((state) => state.isOperationsUser);
  const deleteInFlight = useRef(false);

  const confirmDelete = () => {
    if (deleteInFlight.current || pendingDelete === null) {
      return;
    }
    deleteInFlight.current = true;
    deleteOperation.mutate(pendingDelete, {
      onSettled: () => {
        deleteInFlight.current = false;
        setPendingDelete(null);
      },
    });
  };

  const canOpenVesselCall = useAuthStore((state) =>
    checkPermission(state.userAccessPermissions, "hasVesselCallAccess"),
  );
  const standalone = vesselCallId === undefined;
  const operationPath = useCallback(
    (row: Record<string, unknown>) =>
      standalone && canOpenVesselCall
        ? cargoOpsOperationPath({ id: row.id, vesselCallId: row.vesselCallId })
        : String(row.id),
    [standalone, canOpenVesselCall],
  );
  const openOperation = (row: Record<string, unknown>) => navigate(operationPath(row));

  const headers: Header[] = useMemo(
    () => [
      {
        field: "operationNumber",
        columnName: t(`${L}.columns.id`),
        flex: 0.8,
        minWidth: 120,
        renderCell: (row) => (
          <Link
            component={RouterLink}
            underline="hover"
            to={operationPath(row)}
            onClick={(event) => event.stopPropagation()}
            data-testid={`drybulk-cargo-operations-number-${row.id}`}
          >
            {String(row.operationNumber)}
          </Link>
        ),
      },
      {
        field: "operationTypeLabel",
        columnName: t(`${L}.columns.operation-type`),
        flex: 1,
        minWidth: 110,
      },
      { field: "quantity", columnName: t(`${L}.columns.quantity`), flex: 0.8, minWidth: 90 },
      { field: "size", columnName: t(`${L}.columns.grade-size`), flex: 1, minWidth: 90 },
      { field: "ats", columnName: t(`${L}.columns.ats`), flex: 1, minWidth: 110 },
      { field: "atc", columnName: t(`${L}.columns.atc`), flex: 1, minWidth: 110 },
      {
        field: "statusLabel",
        columnName: t(`${L}.columns.status`),
        badgeType: (row) => operationStatusBadgeType(row.status as string),
        flex: 2,
        minWidth: 210,
      },
    ],
    [t, operationPath],
  );

  const availableFields = useMemo((): Record<string, FilterFieldDefinition> => {
    const status: FilterFieldDefinition = {
      label: t(`${L}.filter-status`),
      type: "select",
      options: OPERATION_STATUSES.map((value) => ({
        label: statusLabel(t, value),
        value,
      })),
    };
    const nominationRefOrderNumber: FilterFieldDefinition = {
      label: t(`${DRYBULK_CARGO_OPS_LANG_BASE}.nomination-filter`),
      type: "text",
    };
    if (isShipper) {
      return { status, nominationRefOrderNumber };
    }
    return {
      operationNumber: { label: t(`${L}.columns.id`), type: "text" },
      operationType: {
        label: t(`${L}.filter-operation-type`),
        type: "select",
        options: operationTypeOptions(t),
      },
      quantity: { label: t(`${L}.columns.quantity`), type: "numberFromTo" },
      gradeSize: {
        label: t(`${L}.filter-grade-size`),
        type: "autocomplete",
        options: gradeOptions.options,
      },
      ats: { label: t(`${L}.columns.ats`), type: "dateFromTo" },
      atc: { label: t(`${L}.columns.atc`), type: "dateFromTo" },
      status,
      nominationRefOrderNumber,
    };
  }, [t, isShipper, gradeOptions.options]);

  const rows = grid.rows.map((row: CargoOperationGridRow) => ({
    id: row.id,
    operationNumber:
      row.operationNumber === null || row.operationNumber === undefined
        ? "-"
        : String(row.operationNumber),
    operationTypeLabel: row.operationType
      ? t(
          `${DRYBULK_CARGO_OPS_LANG_BASE}.add.operation-type-${
            row.operationType === "LOAD" ? "loading" : "unloading"
          }`,
        )
      : "-",
    quantity: row.totalQuantity === null ? "-" : String(row.totalQuantity),
    size: row.gradeSizes && row.gradeSizes.length > 0 ? row.gradeSizes.join(", ") : "-",
    ats: formatGridInstant(row.ats),
    atc: formatGridInstant(row.atc),
    statusLabel: statusLabel(t, row.status),
    status: row.status,
    vesselCallId: row.vesselCallId ?? null,
  }));

  return (
    <div data-testid="drybulk-cargo-operations-list" className="relative">
      {vesselCallId && vesselImo && !isOperationsUser && (
        <AddOperationAction vesselImo={vesselImo} vesselCallId={vesselCallId} />
      )}
      {grid.isError ? (
        <ErrorHandler error={grid.error} testId="drybulk-cargo-operations-grid-error" />
      ) : (
        <>
          <LoadingOverlay loading={grid.isLoading} testId="drybulk-cargo-operations-grid-loading" />
          <CustomDataTable
            testId="drybulk-cargo-operations-grid"
            title={t(`${L}.title`)}
            actionName=""
            showHeaderWhenEmpty
            customEmptyMessagePrefix={`${L}.empty`}
            headers={headers}
            rows={rows}
            page={grid.page}
            pageSize={grid.pageSize}
            totalCount={grid.totalRecordCount}
            onPageChange={grid.changePage}
            onRowClick={(row) => openOperation(row)}
            actionColumn={{
              actionsDisplay: "menu",
              actions: [
                {
                  label: t(`${L}.open`),
                  testId: (row) => `drybulk-cargo-operations-open-${row.id}`,
                  variant: "text",
                  onClick: (row) => openOperation(row),
                },
                {
                  label: t(`${L}.delete`),
                  testId: (row) => `drybulk-cargo-operations-delete-${row.id}`,
                  variant: "text",
                  color: "error",
                  isVisible: (row) => row.status === "DRAFT" && isShippingAgent,
                  onClick: (row) => setPendingDelete(String(row.id)),
                },
              ],
            }}
            navigationLinkBase=""
            filters={grid.filters}
            onApplyFilters={grid.applyFilters}
            onClearFilters={grid.clearFilters}
            availableFields={availableFields}
            pageSizeOptions={GRID_PAGE_SIZE_OPTIONS}
          />
        </>
      )}
      <DeleteDraftDialog
        open={pendingDelete !== null}
        isDeleting={deleteOperation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => {
          if (!deleteOperation.isPending) {
            setPendingDelete(null);
          }
        }}
      />
    </div>
  );
};

export default DrybulkCargoOperationsList;
